/**
 * Notifications API module.
 *
 * Covers the whole template-led notification surface:
 *
 *  - the flat and grouped in-app lists, plus coalesced threads;
 *  - `GET /event-types/`, the catalogue both the admin grid and the in-app
 *    type filter render from (nothing hardcodes a taxonomy);
 *  - tenant overrides (enable/disable, channels, frequency per event);
 *  - the tenant master switches, including the email kill switch;
 *  - per-user subscriptions (mute a domain/subdomain/event, digest cadence).
 *
 * Every path is relative to the axios `baseURL`, so they resolve under
 * `/api/notifications/`.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  Notification,
  NotificationChannel,
  NotificationDeleteResult,
  NotificationDigestFrequency,
  NotificationDigestPreviewKind,
  NotificationDomain,
  NotificationEventType,
  NotificationEventTypeCatalogue,
  NotificationEventTypeParams,
  NotificationFrequency,
  NotificationListParams,
  NotificationMutationResult,
  NotificationOverrideBulkPayload,
  NotificationOverrideBulkResult,
  NotificationOverrideInput,
  NotificationOverrideParams,
  NotificationPreferences,
  NotificationRecentParams,
  NotificationSettings,
  NotificationSettingsUpdate,
  NotificationSeverity,
  NotificationSubscription,
  NotificationSubscriptionBulkResult,
  NotificationSubscriptionClearResult,
  NotificationSubscriptionInput,
  NotificationSubscriptionParams,
  NotificationSubscriptionScopeRef,
  NotificationSubscriptionSide,
  NotificationSubscriptionTree,
  NotificationInternalOptIn,
  NotificationSubscriptionUpdate,
  NotificationTemplateOverride,
  NotificationTypeChoice,
  SendBudgetResponse,
  UnreadCount,
} from './types';

/**
 * Fixed render order for domain sections — matches the backend's
 * `DOMAIN_DISPLAY_ORDER`. Use it to sort groups the API returns unsorted.
 */
export const NOTIFICATION_DOMAIN_ORDER: readonly NotificationDomain[] = [
  'purchasing',
  'quality',
  'compliance',
  'onboarding',
  'rfq',
  'system',
];

/**
 * Severity rank, highest urgency last — mirrors the backend `SEVERITY_RANK`.
 * Compare two tiers with `NOTIFICATION_SEVERITY_RANK[a] >= NOTIFICATION_SEVERITY_RANK[b]`.
 */
export const NOTIFICATION_SEVERITY_RANK: Record<NotificationSeverity, number> = {
  background: 0,
  info: 1,
  action_required: 2,
  critical: 3,
};

/**
 * Cadence values in "loudest first" order — the order the admin frequency
 * selector renders them in. `off` last, because it is the quiet end of the
 * scale: bell only, never email.
 */
export const NOTIFICATION_FREQUENCY_ORDER: readonly NotificationFrequency[] = [
  'immediate',
  'hourly',
  'daily',
  'weekly',
  'off',
];

/** One row of the bulk-save body, as the backend's grid endpoint names it. */
interface NotificationOverrideRow {
  template_key: string;
  is_enabled?: boolean | null;
  channels?: NotificationChannel[] | null;
  frequency?: NotificationFrequency | null;
  dedup_window_minutes?: number | null;
  audience_rule?: number | null;
  reset?: boolean;
}

/**
 * Map one override to the grid endpoint's row shape.
 *
 * The single-row endpoints speak `channel_override`; the bulk endpoint speaks
 * `channels`. `frequency` is spelled the same on both. Translating here keeps
 * every caller on one input type.
 */
function toOverrideRow(override: NotificationOverrideInput): NotificationOverrideRow {
  const row: NotificationOverrideRow = { template_key: override.template_key };
  if (override.is_enabled !== undefined) row.is_enabled = override.is_enabled;
  if (override.channel_override !== undefined) row.channels = override.channel_override;
  if (override.frequency !== undefined) row.frequency = override.frequency;
  if (override.dedup_window_override !== undefined) {
    row.dedup_window_minutes = override.dedup_window_override;
  }
  if (override.audience_rule_override !== undefined) {
    row.audience_rule = override.audience_rule_override;
  }
  return row;
}

/**
 * The optional scope claim on a config write.
 *
 * Every notification-config response says which company it resolved to; echoing
 * that id back on the write is what lets the API refuse — with `409
 * scope_mismatch` — a save whose screen has since been re-scoped, instead of
 * quietly landing it on a company the admin is not looking at.
 *
 * Omitted when the caller has no id to claim, which is the pre-existing
 * behaviour the backend still accepts.
 *
 * @param company - the resolved company id from the response's scope block.
 */
function scopeClaim(company?: number): { company?: number } {
  return typeof company === 'number' ? { company } : {};
}

export const notificationsAPI = {
  // -------------------------------------------------------------------------
  // In-app list
  // -------------------------------------------------------------------------

  /** Flat paginated list. Legacy filters still work alongside the new ones. */
  getNotifications: (
    params?: NotificationListParams
  ): Promise<AxiosResponse<PaginatedResponse<Notification>>> =>
    api.get('/notifications/', { params }),

  /** Single notification. */
  getNotification: (id: number): Promise<AxiosResponse<Notification>> =>
    api.get(`/notifications/${id}/`),

  /** Delete a single notification. */
  deleteNotification: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/notifications/${id}/`),

  /**
   * Unread totals for the bell, optionally broken down by domain/severity.
   *
   * @param params.domains - CSV of domain slugs the caller can see. Pass the
   *   same list the popover filters by, or the badge counts rows the popover
   *   refuses to show. Legacy rows (no domain) are always counted.
   */
  getUnreadCount: (params?: {
    min_severity?: NotificationSeverity;
    include_background?: boolean;
    domains?: string;
    breakdown?: boolean;
  }): Promise<AxiosResponse<UnreadCount>> =>
    api.get('/notifications/unread_count/', { params }),

  /** Recent notifications for the TopBar / sidebar bell popover. */
  getRecent: (
    params?: NotificationRecentParams
  ): Promise<AxiosResponse<Notification[]>> =>
    api.get('/notifications/recent/', { params }),

  /** Mark one notification read. */
  markAsRead: (id: number): Promise<AxiosResponse<Notification>> =>
    api.post(`/notifications/${id}/mark_read/`),

  /** Mark every notification read. */
  markAllAsRead: (): Promise<AxiosResponse<NotificationMutationResult>> =>
    api.post('/notifications/mark_all_read/'),

  /** Mark an explicit set of notifications read. */
  markSelectedAsRead: (
    notificationIds: number[]
  ): Promise<AxiosResponse<NotificationMutationResult>> =>
    api.post('/notifications/mark_selected_read/', { notification_ids: notificationIds }),

  /** Delete every already-read notification. */
  deleteAllRead: (): Promise<AxiosResponse<NotificationDeleteResult>> =>
    api.post('/notifications/delete_all_read/'),

  // Threads are assembled client-side from the flat list by
  // `components/notifications/groupNotifications.ts` using each row's
  // `coalesce_key` / `thread_id`. There is deliberately no thread endpoint
  // here: functions pointing at unrouted paths only ever 404 at runtime.

  // -------------------------------------------------------------------------
  // Template catalogue
  // -------------------------------------------------------------------------

  /**
   * The event catalogue, with this tenant's effective config resolved per row.
   * Backs both the admin grid and the in-app type filter — never hardcode a
   * notification taxonomy in a component.
   */
  getEventTypes: (
    params?: NotificationEventTypeParams
  ): Promise<AxiosResponse<NotificationEventTypeCatalogue>> =>
    api.get('/notifications/event-types/', { params }),

  /** One catalogue row, addressed by its dotted event key. */
  getEventType: (key: string): Promise<AxiosResponse<NotificationEventType>> =>
    api.get(`/notifications/event-types/${encodeURIComponent(key)}/`),

  /**
   * Legacy `{value,label}` list off the 40-value enum.
   *
   * @deprecated Use {@link notificationsAPI.getEventTypes} — this predates the
   * catalogue and only covers historic rows.
   */
  getTypes: (): Promise<AxiosResponse<NotificationTypeChoice[]>> =>
    api.get('/notifications/types/'),

  // -------------------------------------------------------------------------
  // Tenant overrides
  // -------------------------------------------------------------------------

  /** Existing override rows for the caller's tenant. */
  getOverrides: (
    params?: NotificationOverrideParams
  ): Promise<AxiosResponse<PaginatedResponse<NotificationTemplateOverride>>> =>
    api.get('/notifications/template-overrides/', { params }),

  /** Create an override for one event. */
  createOverride: (
    data: NotificationOverrideInput
  ): Promise<AxiosResponse<NotificationTemplateOverride>> =>
    api.post('/notifications/template-overrides/', data),

  /** Patch an existing override row. */
  updateOverride: (
    id: number,
    data: Partial<NotificationOverrideInput>
  ): Promise<AxiosResponse<NotificationTemplateOverride>> =>
    api.patch(`/notifications/template-overrides/${id}/`, data),

  /** Delete an override row — the row falls back to the shipped template. */
  deleteOverride: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/notifications/template-overrides/${id}/`),

  /**
   * Reset events to platform default without knowing the override id.
   *
   * The endpoint takes a list, so one round trip resets a whole domain.
   *
   * @param templateKey - one event key or a list of them.
   * @param company - the company the caller believes it is configuring; the
   *   API refuses the reset with `409 scope_mismatch` if it disagrees.
   */
  resetOverride: (
    templateKey: string | string[],
    company?: number
  ): Promise<AxiosResponse<NotificationOverrideBulkResult>> =>
    api.post('/notifications/template-overrides/reset/', {
      template_keys: Array.isArray(templateKey) ? templateKey : [templateKey],
      ...scopeClaim(company),
    }),

  /**
   * The admin grid's single "Save changes" round trip: upsert every edited
   * row and reset every cleared row in one atomic transaction.
   *
   * The endpoint takes one `rows` list in which a reset is a row carrying
   * `reset: true`, so both halves of a save arrive together — an edit can
   * never land without its companion reset.
   */
  bulkSaveOverrides: (
    payload: NotificationOverrideBulkPayload
  ): Promise<AxiosResponse<NotificationOverrideBulkResult>> =>
    api.post('/notifications/template-overrides/bulk_update/', {
      rows: [
        ...payload.overrides.map(toOverrideRow),
        ...(payload.reset_template_keys ?? []).map((key) => ({
          template_key: key,
          reset: true,
        })),
      ],
      // One call configures one company, so the claim is the batch's, not a
      // per-row field.
      ...scopeClaim(payload.company),
    }),

  // -------------------------------------------------------------------------
  // Master switches
  // -------------------------------------------------------------------------

  /** Tenant master switches, quiet hours and frequency caps. */
  getSettings: (): Promise<AxiosResponse<NotificationSettings>> =>
    api.get('/notifications/settings/'),

  /** A daily summary email rendered with sample data + install branding. */
  getDigestPreview: (
    kind: NotificationDigestPreviewKind = 'procurement'
  ): Promise<AxiosResponse<{ subject: string; html: string }>> =>
    api.get('/notifications/settings/digest-preview/', { params: { kind } }),

  /** Patch the tenant master switches. */
  updateSettings: (
    data: NotificationSettingsUpdate
  ): Promise<AxiosResponse<NotificationSettings>> =>
    api.patch('/notifications/settings/', data),

  /**
   * Flip the master email kill switch.
   *
   * @param enabled - false stops every notification email for this tenant,
   *   whatever the templates, overrides and per-user subscriptions say.
   *   Transactional mail (invites, password resets) is unaffected.
   * @param company - the company the caller believes it is silencing; the API
   *   refuses with `409 scope_mismatch` if it disagrees, so the kill switch can
   *   never stop the wrong tenant's email.
   */
  setEmailsEnabled: (
    enabled: boolean,
    company?: number
  ): Promise<AxiosResponse<NotificationSettings>> =>
    api.patch('/notifications/settings/', {
      emails_enabled: enabled,
      ...scopeClaim(company),
    }),

  // -------------------------------------------------------------------------
  // Per-supplier daily send budget
  // -------------------------------------------------------------------------

  /**
   * The site's default daily allowance plus every supplier's own.
   *
   * Returns one row per active relationship, not only the ones carrying an
   * exception, so the picker and the exception list come from one read.
   */
  getSendBudgets: (): Promise<AxiosResponse<SendBudgetResponse>> =>
    api.get('/notifications/settings/send-budgets/'),

  /**
   * Set or clear one supplier's daily allowance.
   *
   * @param supplier - supplier company id; must already have a relationship
   *   with the site being configured, or the API answers 404.
   * @param maxEmailsPerDay - distinct sends per day. `0` clears the exception
   *   and restores the site default.
   * @param company - the company the caller believes it is configuring; a
   *   disagreement is a `409 scope_mismatch` rather than a write to the wrong
   *   tenant.
   */
  setSendBudget: (
    supplier: number,
    maxEmailsPerDay: number,
    company?: number
  ): Promise<AxiosResponse<SendBudgetResponse>> =>
    api.post('/notifications/settings/send-budgets/', {
      supplier,
      max_emails_per_day: maxEmailsPerDay,
      ...scopeClaim(company),
    }),

  // -------------------------------------------------------------------------
  // Per-user subscriptions
  // -------------------------------------------------------------------------

  /** The caller's raw subscription rows. */
  getSubscriptions: (
    params?: NotificationSubscriptionParams
  ): Promise<AxiosResponse<PaginatedResponse<NotificationSubscription>>> =>
    api.get('/notifications/subscriptions/', { params }),

  /**
   * The settings-page tree: global -> domain -> subdomain -> event, each node
   * carrying its own row plus the effective values it inherits.
   */
  getSubscriptionTree: (
    side?: NotificationSubscriptionSide
  ): Promise<AxiosResponse<NotificationSubscriptionTree>> =>
    api.get('/notifications/subscriptions/tree/', {
      params: side ? { side } : undefined,
    }),

  /**
   * Upsert the row for one scope + channel.
   *
   * `POST /subscriptions/` is an upsert on `(user, scope_type, scope_key,
   * channel)`, so this is safe to call whether or not a row exists — which is
   * what makes a *first* mute work without a prior read.
   */
  setSubscription: (
    data: NotificationSubscriptionInput
  ): Promise<AxiosResponse<NotificationSubscription>> =>
    api.post('/notifications/subscriptions/', data),

  /** Patch an existing subscription row by id. */
  updateSubscription: (
    id: number,
    data: NotificationSubscriptionUpdate
  ): Promise<AxiosResponse<NotificationSubscription>> =>
    api.patch(`/notifications/subscriptions/${id}/`, data),

  /** Delete a subscription row by id — the scope reverts to inherited. */
  deleteSubscription: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/notifications/subscriptions/${id}/`),

  /** Mute a domain, subdomain or single event (optionally one channel only). */
  muteScope: (
    scope: NotificationSubscriptionScopeRef
  ): Promise<AxiosResponse<NotificationSubscription>> =>
    api.post('/notifications/subscriptions/', { ...scope, muted: true }),

  /** Unmute a previously muted scope. */
  unmuteScope: (
    scope: NotificationSubscriptionScopeRef
  ): Promise<AxiosResponse<NotificationSubscription>> =>
    api.post('/notifications/subscriptions/', { ...scope, muted: false }),

  /** Set digest cadence at a scope, e.g. daily for the whole Quality domain. */
  setDigestFrequency: (
    scope: NotificationSubscriptionScopeRef,
    frequency: NotificationDigestFrequency
  ): Promise<AxiosResponse<NotificationSubscription>> =>
    api.post('/notifications/subscriptions/', {
      ...scope,
      digest_frequency: frequency,
    }),

  /** CR-7255 review: opt in or out of one domain + channel of supplier-facing
   *  mail from customers in the person's own group. */
  setInternalOptIn: (
    domain: string,
    channel: 'bell' | 'email',
    enabled: boolean
  ): Promise<AxiosResponse<{ internal_opt_in: NotificationInternalOptIn | null }>> =>
    api.post('/notifications/subscriptions/internal-opt-in/', { domain, channel, enabled }),

  /** Save many subscription rows at once (the settings screen's save). */
  bulkSetSubscriptions: (
    rows: NotificationSubscriptionInput[],
    side?: NotificationSubscriptionSide
  ): Promise<AxiosResponse<NotificationSubscriptionBulkResult>> =>
    api.post('/notifications/subscriptions/bulk_set/', { rows }, {
      params: side ? { side } : undefined,
    }),

  /** Delete the row for one scope without knowing its id. */
  clearSubscription: (
    scope: NotificationSubscriptionScopeRef
  ): Promise<AxiosResponse<NotificationSubscriptionClearResult>> =>
    api.post('/notifications/subscriptions/reset/', {
      scope_type: scope.scope_type,
      scope_key: scope.scope_key ?? '',
    }),

  /** Delete every subscription row — back to tenant defaults everywhere. */
  resetSubscriptions: (): Promise<AxiosResponse<NotificationSubscriptionClearResult>> =>
    api.post('/notifications/subscriptions/reset/', { all: true }),

  // -------------------------------------------------------------------------
  // Legacy preference row (kept as the backward-compatible opt-out)
  // -------------------------------------------------------------------------

  /** The legacy 19-boolean preference row. */
  getPreferences: (): Promise<AxiosResponse<NotificationPreferences>> =>
    api.get('/notifications/preferences/'),

  /** Replace the legacy preference row. */
  updatePreferences: (
    data: Partial<NotificationPreferences>
  ): Promise<AxiosResponse<NotificationPreferences>> =>
    api.put('/notifications/preferences/', data),

  /** Patch the legacy preference row. */
  patchPreferences: (
    data: Partial<NotificationPreferences>
  ): Promise<AxiosResponse<NotificationPreferences>> =>
    api.patch('/notifications/preferences/', data),
};
